import * as Format from "@animech/axiom-format";
import * as SceneBuilder from "@animech/axiom-scene-builder";
import * as WallBuilder from "@animech/axiom-wall-builder";
import * as Configurator from "@animech/axiom-configurator";
import * as Utility from "@animech/axiom-utility";
import * as ConfigurationLogic from "@animech/axiom-configuration-logic";
import * as AxiomUiBuilder from "@animech/axiom-ui-builder";
import {
	CustomNode,
	CustomSceneTree,
	Root,
	CustomCameraSettings,
} from "@definitions";

type NodeId = string;
type ImageId = string;
type MaterialId = string;
type ModelId = string;
type Locale = string;
type UiTerm = string;

export type Property<
	TValue = unknown,
	TPath extends Format.Path = Format.Path,
> = Format.Property<Root, TValue, TPath>;
export type PropertyValue = unknown | PropertyValue[];
export type PropertyDelta = Configurator.PropertyDelta<Root>;
export type PropertyChangeMap = Configurator.PropertyChangeMap<Root>;
export const PropertyStateMap = Configurator.PropertyStateMap<Root>;
export type PropertyStateMap = Configurator.PropertyStateMap<Root>;

export type ConfigurationValue<TValue> = Format.ConfigurationValue<
	Root,
	TValue
>;

export type ConfigurationHandler =
	ConfigurationLogic.ConfigurationHandler<Root>;
export const DefaultConfigurationHandler =
	ConfigurationLogic.DefaultConfigurationHandler<
		Root,
		Format.RuleApiExtensions
	>;

export type UiBuilder = AxiomUiBuilder.UiBuilder<Root>;
export type UiBuilderCallbacks = AxiomUiBuilder.UiBuilderCallbacks<Root>;
export type MetaRegistry = AxiomUiBuilder.MetaRegistry<
	Root,
	ArticleItemId,
	ImageId,
	Property,
	CustomPropertyValueMeta
>;

type CustomPropertyMeta = {
	//
};

type CustomPropertyValueMeta = {
	//
};

export type ArticleCategoryId = string;
export type ArticleItemId = string;

export type Localizer = AxiomUiBuilder.Localizer<
	Root,
	ArticleCategoryId,
	ArticleItemId,
	ImageId,
	AnyArticleStructure,
	NodeId,
	Locale,
	UiTerm,
	CustomPropertyMeta,
	CustomPropertyValueMeta,
	Property
>;

export type AnyArticleStructure = Format.ArticleStructure<
	ArticleCategoryId,
	ArticleItemId,
	ImageId
>;

export type PropertyMetaTree = Format.PropertyMetaTree<
	Format.RootProperty<Root>,
	PropertyMeta,
	PropertyValueMeta,
	ImageId
>;
export type PropertyMetaEntry = Format.PropertyMetaEntry<
	Property,
	PropertyMeta,
	PropertyValueMeta
>;
export type PropertyMeta = Format.PropertyMeta<ArticleItemId, ImageId>;
export type PropertyValueMeta = Format.PropertyValueMeta<
	ArticleItemId,
	ImageId
>;

export type UiVisibilityFunction = AxiomUiBuilder.UiVisibilityFunction<Root>;

export type ProductId = string;
export type ProductTemplate = {
	template?: Format.ArrayProperty<Root, unknown[], Format.Path>;
	value?: unknown;
};
export type ProductStructure = Format.ProductStructure<
	ProductId,
	ProductTemplate
>;

export type Model = Format.Model<Root, ModelId>;
export type Shape = Format.Shape<Root>;
export type Repeat = Format.Repeat<Root>;
export type Transform = Format.Transform<Root, MaterialId>;
export type ControllerType = string;
export type SceneStructure = Format.SceneStructure<
	Root,
	ModelId,
	MaterialId,
	CameraViewId,
	ControllerType,
	CustomSceneTree,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type SceneTree = Format.SceneTree<
	Root,
	ModelId,
	MaterialId,
	ControllerType,
	CustomSceneTree,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type SceneObject = SceneBuilder.SceneObject<Root>;

export type CameraView = Format.CameraView;
export type CameraViewId = string;
export type CameraViews = Format.CameraViews<CameraViewId>;
export type CameraSettings = Format.CameraSettings<CameraViewId> &
	CustomCameraSettings;
export type CameraViewSettings = Format.CameraViewSettings;

export type AttacherTree = Format.AttacherTree<Root, ModelId, MaterialId>;
export type AttacherObject = SceneBuilder.AttacherObject<Root>;
export type AttacherSolution = SceneBuilder.AttacherSolution<Root>;

export type ActionContext = Utility.ActionContext<Root>;
export type OnAction = Utility.OnAction<Root>;

export type WallObject = WallBuilder.WallObject<Root>;
export type WallDoorObject = WallBuilder.WallDoorObject<Root>;
export type WallWindowObject = WallBuilder.WallWindowObject<Root>;
export type AnyWallEditingTool = WallBuilder.AnyWallEditingTool<Root>;
export type AnyWallEditingToolType = WallBuilder.AnyWallEditingToolType<Root>;
export type WallInteractionContext = WallBuilder.WallInteractionContext<Root>;

export type UiStructure = Format.UiStructure<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;

export type UiSlots = Format.UiSlots<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type UiSettings = Format.UiSettings<Root>;
export type UiFavicon = Format.UiFavicon<ImageId>;
export type UiLogos = Format.UiLogos<ImageId>;
export type UiLogoSources = Format.UiLogoSources<ImageId>;

export type UiTree = Format.UiTree<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type UiNode = Format.UiNode<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type GroupNode = Format.GroupNode<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type BaseNode = Format.BaseNode<Root, NodeId, ImageId>;
export type FragmentNode = Format.FragmentNode<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type TransformNode = Format.TransformNode<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type ScrollAnchorNode = Format.ScrollAnchorNode<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type TreeNavNode = Format.TreeNavNode<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type TreeNavItemNode = Format.TreeNavItemNode<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type TreeNavChild = Format.TreeNavChild<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type TabFrameNode = Format.TabFrameNode<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type GridNode = Format.GridNode<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type CanvasNode = Format.CanvasNode<Root, NodeId, ImageId>;
export type CompositePropertyNode = Format.CompositePropertyNode<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type ProductListNode = Format.ProductListNode<
	Root,
	NodeId,
	ProductId,
	ImageId,
	CustomNode
>;
export type PropertyNode = Format.PropertyNode<Root, unknown, NodeId, ImageId>;
export type GeneralPropertyNode = Format.GeneralPropertyNode<
	Root,
	unknown,
	NodeId,
	ImageId
>;
export type NumberInputNode = Format.NumberInputNode<
	Root,
	unknown,
	NodeId,
	ImageId
>;
export type SortNode = Format.SortNode<Root, NodeId, ImageId>;
export type FilterNode = Format.FilterNode<Root, NodeId, ImageId>;

export type OptionalPropertyNodeKeys = Format.OptionalPropertyNodeKeys;
